import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Box,
    Typography,
    TextField,
    IconButton,
    Select,
    MenuItem,
    FormControl,
    InputLabel
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PrintIcon from '@mui/icons-material/Print';
import { styled, keyframes } from '@mui/system';
import { printCustomerReport } from '../Reports/Print/printCustomerReport';
import ScrollToTopButton from '../Button/ScrollToTopButton';
import HomeIcon from '@mui/icons-material/Home';
import { motion } from 'framer-motion';
import { useNavigate } from "react-router-dom";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Link } from 'react-router-dom';

const slideInFromLeft = keyframes`
  0% {
    transform: translateX(-20px);
    opacity: 0;
  }
  100% {
    transform: translateX(0);
    opacity: 1;
  }
`;

const AnimatedTypography = styled(Typography)(({ theme }) => ({
    animation: `${slideInFromLeft} 0.5s ease-out`,
}));

const CustomerReport = () => {
    const [user, setUser] = useState({});
    const [token, setToken] = useState(() => localStorage.getItem('token') || null);
    const [loading, setLoading] = useState(false);
    const [fetchedData, setFetchedData] = useState([]);
    const [damages, setDamages] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedMonth, setSelectedMonth] = useState('');
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [isSearchOpen, setIsSearchOpen] = useState(false);
    const navigate = useNavigate();

    // Month options for dropdown
    const months = [
        { value: '', label: 'All Months' },
        { value: '01', label: 'January' },
        { value: '02', label: 'February' },
        { value: '03', label: 'March' },
        { value: '04', label: 'April' },
        { value: '05', label: 'May' },
        { value: '06', label: 'June' },
        { value: '07', label: 'July' },
        { value: '08', label: 'August' },
        { value: '09', label: 'September' },
        { value: '10', label: 'October' },
        { value: '11', label: 'November' },
        { value: '12', label: 'December' }
    ];

    const handleMonthChange = (event) => {
        setSelectedMonth(event.target.value);
    };

    // Generate year options (current year and previous 5 years)
    const generateYearOptions = () => {
        const currentYear = new Date().getFullYear();
        const years = [];
        for (let i = 0; i < 6; i++) {
            years.push(currentYear - i);
        }
        return years;
    };

    const toggleSearch = () => {
        setIsSearchOpen(!isSearchOpen);
    };

    useEffect(() => {
        if (token) {
            localStorage.setItem('token', token);
        }
    }, [token]);

    useEffect(() => {
        const fetchProfile = async () => {
            const token = localStorage.getItem("token");

            if (!token) {
                navigate("/"); // Redirect if no token
                return;
            }

            try {
                const response = await axios.get("https://admin.bizstor.lk/public/api/profile", {
                    headers: { Authorization: `Bearer ${token}` },
                });
                setUser(response.data);
            } catch (error) {
                if (error.response && error.response.status === 401) {
                    localStorage.removeItem("token");
                    navigate("/");
                }
            }
        };

        fetchProfile();
    }, []);

    useEffect(() => {
        if (user.id) {
            fetchData();
        }
    }, [user.id]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const response = await axios.get('https://admin.bizstor.lk/public/api/get-data');
            const damagesResponse = await axios.get('https://admin.bizstor.lk/public/api/get-damage-data');

            if (user.id) {
                const Orders = response.data.filter(order =>
                    order.usertoken.toLowerCase() === user.id.toString().toLowerCase()
                );
                setFetchedData(Orders);

                const filteredDamages = damagesResponse.data.filter(damage =>
                    damage.type === "money back" &&
                    damage.usertoken.toLowerCase() === user.id.toString().toLowerCase()
                );
                setDamages(filteredDamages);
            }
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    // Filter data by month and year
    const filterDataByMonth = (data, month, year) => {
        if (!month) return data;

        return data.filter(order => {
            // Assuming your order has a date field - adjust the field name as needed
            const orderDate = new Date(order.invoicedate || order.date || order.order_date);
            const orderMonth = String(orderDate.getMonth() + 1).padStart(2, '0');
            const orderYear = orderDate.getFullYear();

            return orderMonth === month && orderYear === year;
        });
    };

    const filterData = (query, data) => {
        if (!query) return data;
        return data.filter((d) =>
            d.fullname?.toLowerCase().includes(query.toLowerCase())
        );
    };

    const calculateTotals = (data) => {
        const totalsByCustomer = {};

        data.forEach(order => {
            const customerName = order.fullname;
            let orderTotal = 0;

            order.items.forEach(item => {
                const itemTax = (Number(item.price) * (item.tax / 100)) || 0;
                const itemTotal = Number(item.price) + itemTax;
                orderTotal += itemTotal;
            });

            const discountPercentage = order.discount || 0;
            const discountAmount = (orderTotal * discountPercentage) / 100;
            const finalAmount = orderTotal - discountAmount;

            if (totalsByCustomer[customerName]) {
                totalsByCustomer[customerName] += finalAmount;
            } else {
                totalsByCustomer[customerName] = finalAmount;
            }
        });

        return totalsByCustomer;
    };

    const calculateDamageTotals = (damageData) => {
        const damageTotalsByCustomer = {};

        damageData.forEach(damage => {
            const customerName = damage.fullname;
            let damageTotal = 0;

            damage.items.forEach(item => {
                damageTotal += Number(item.price) || 0;
            });

            if (damageTotalsByCustomer[customerName]) {
                damageTotalsByCustomer[customerName] += damageTotal;
            } else {
                damageTotalsByCustomer[customerName] = damageTotal;
            }
        });

        return damageTotalsByCustomer;
    };

    // Filter data by selected month/year
    const monthFilteredData = filterDataByMonth(fetchedData, selectedMonth, selectedYear);
    const monthFilteredDamages = filterDataByMonth(damages, selectedMonth, selectedYear);

    const uniqueData = Array.from(new Set(monthFilteredData.map(order => order.fullname)))
        .map(fullname => {
            const order = monthFilteredData.find(order => order.fullname === fullname);
            return {
                ...order,
                total: calculateTotals(monthFilteredData)[fullname] || 0
            };
        });

    const dataFiltered = filterData(searchQuery, uniqueData, user);

    const damageTotals = calculateDamageTotals(monthFilteredDamages);

    const finalData = dataFiltered.map(order => {
        const orderTotal = order.total;
        const damageTotal = damageTotals[order.fullname] || 0;

        return {
            ...order,
            adjustedTotal: orderTotal - damageTotal
        };
    });

    const grandTotal = finalData.reduce((sum, order) => sum + order.adjustedTotal, 0);

    const handleBackClick = () => {
        navigate("/allreport");
    };

    const getSelectedMonthName = () => {
        if (!selectedMonth) return 'All Months';
        const month = months.find(m => m.value === selectedMonth);
        return month ? month.label : '';
    };

    return (
        <Box
        sx={{mt:10}}
        >
            <AnimatedTypography
                component="h1"
                sx={{
                    ml: { xs: 2, sm: 3 },
                    mt: { xs: 2, sm: 3 },
                    color: '',
                    letterSpacing: '0.01em',
                    fontSize: { xs: '20px', sm: '24px' },
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', ml:1 }}>
                    {/**<Typography
                        component="span"
                        sx={{
                            display: { xs: 'none', sm: 'inline' },
                            fontSize: { xs: '14px', sm: '16px' },
                            fontWeight: 'bold',
                            color: '',
                        }}
                    >
                        Sales By Customer
                    </Typography>
                    <Typography
                        component="span"
                        sx={{
                            display: { xs: 'none', sm: 'inline' },
                            ml: 1,
                            fontSize: '14px',
                            color: '',
                        }}
                    >
                        |
                    </Typography>*/}
                    <HomeIcon
                        sx={{
                            ml: 1,
                            fontSize: '18px',
                            color: '#6d7081',
                        }}
                    />
                    <Typography
                        component="span"
                        sx={{
                            ml: 0.5,
                            fontSize: '14px',
                            color: '#6d7081',
                        }}
                    >
                        - Report - Sales By Customer
                    </Typography>
                </Box>
            </AnimatedTypography>

            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    width:  {sm:'95%',xs:'90%'},
                    maxWidth: 'auto',
                    margin: 'auto',
                    padding: '10px',
                    bgcolor: 'background.paper',
                    border: '1px solid #EDF2F7',
                    borderRadius: '16px',
                    marginTop: '30px',
                    position: 'relative',
                    '@media (max-width: 600px)': {
                        marginTop: '10px',
                    },
                    height: 'auto',
                    mb: 2
                }}
            >
                <Typography variant="h4" >

                </Typography>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' }, // Responsive direction
                        justifyContent: 'space-between', // Space between left and right groups
                        alignItems: 'center', // Align items vertically
                        p: 2, // Padding inside the box
                        gap: 2, // Space between sections

                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'left',
                            '@media (max-width: 600px)': {
                                margin: 'auto',
                            },

                        }}
                    >
                        <Box sx={{ color: '#1e293b', }}><h2>Sales By Customer Summary</h2></Box>
                        <Box
                            component="div"
                            sx={{
                                mb: { sm: 2, xs: 'none' },
                                cursor: 'pointer',
                                display: 'inline-flex',
                                alignItems: 'center',
                                color: 'primary.main',
                                width: 'fit-content',
                                '&:hover': {
                                    color: 'primary.main',
                                }
                            }}
                            onClick={handleBackClick}
                        >
                            <ArrowBackIcon fontSize="small" />
                            <Typography
                                variant="body2"
                                sx={{
                                    ml: 0.5,
                                    fontWeight: 500
                                }}
                            >
                                Back to Report
                            </Typography>
                        </Box>

                    </Box>

                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: isSearchOpen ? 1 : 0, height: isSearchOpen ? 'auto' : 0 }}
                        transition={{ duration: 0.3 }}
                        style={{ overflow: 'hidden', }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: { xs: 'column', sm: 'row' }, // Stack vertically on small screens
                                gap: 1, // Space between the TextFields
                                width: '80%', // Ensure it takes full width on smaller screens
                                alignItems: 'center', // Align items vertically
                                '@media (max-width: 600px)': {
                                    margin: 'auto',
                                },
                            }}
                        >
                            <Box>
                                <TextField
                                    variant="outlined"
                                    margin="normal"
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    value={searchQuery}
                                    size="small"
                                    placeholder="Search…"
                                    InputProps={{
                                        startAdornment: (
                                            <IconButton sx={{ padding: 0, mr: 1 }}>
                                                <SearchIcon />
                                            </IconButton>
                                        ),
                                    }}
                                    sx={{
                                        width: '250px',
                                        borderRadius: '8px',
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '8px',
                                        },
                                    }}
                                />
                            </Box>
                            <Box>
                                <FormControl
                                    size="small"
                                    sx={{
                                        width: '250px',
                                        height: '40px',
                                        borderRadius: '8px',
                                        bgcolor: 'rgba(240, 240, 240, 0.17)',
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '8px',
                                        },
                                    }}
                                >
                                    <Select
                                        value={selectedMonth}
                                        onChange={handleMonthChange}
                                        displayEmpty
                                        size="small"
                                        sx={{
                                            borderRadius: '8px',
                                        }}
                                    >
                                        <MenuItem value="">
                                            <em>All Months</em>
                                        </MenuItem>
                                        <MenuItem value="01">January</MenuItem>
                                        <MenuItem value="02">February</MenuItem>
                                        <MenuItem value="03">March</MenuItem>
                                        <MenuItem value="04">April</MenuItem>
                                        <MenuItem value="05">May</MenuItem>
                                        <MenuItem value="06">June</MenuItem>
                                        <MenuItem value="07">July</MenuItem>
                                        <MenuItem value="08">August</MenuItem>
                                        <MenuItem value="09">September</MenuItem>
                                        <MenuItem value="10">October</MenuItem>
                                        <MenuItem value="11">November</MenuItem>
                                        <MenuItem value="12">December</MenuItem>
                                    </Select>
                                </FormControl>
                            </Box>
                        </Box>
                    </motion.div>
                    <Box
                        sx={{
                            display: 'flex',
                            gap: 1,
                        }}
                    ><Box>
                            <IconButton
                                onClick={toggleSearch}
                                sx={{
                                    display: { xs: 'flex', sm: 'none' },
                                }}
                            >
                                <SearchIcon />
                            </IconButton>
                        </Box>
                        <Box>

                        </Box>
                        <Box
                            sx={{
                                mt: { xs: 'none', sm: '15px' }
                            }}
                        >
                            <IconButton
                                aria-label="print"
                                onClick={() => printCustomerReport(finalData)}
                            >
                                <PrintIcon />
                            </IconButton>
                        </Box>

                    </Box>
                </Box>

                <Box
                    sx={{
                        display: { sm: 'flex', xs: 'none' },
                        flexDirection: 'row',
                        width: '100%',
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '20%',
                        }}
                    >

                        <TextField
                            variant="outlined"
                            margin="normal"
                            onChange={(e) => setSearchQuery(e.target.value)}
                            value={searchQuery}
                            size="small"
                            placeholder="Search…"
                            InputProps={{
                                startAdornment: (
                                    <IconButton sx={{ padding: 0, mr: 1 }}>
                                        <SearchIcon />
                                    </IconButton>
                                ),
                            }}
                            sx={{
                                mt: 1,
                                bgcolor: 'rgba(240, 240, 240, 0.17)',
                                width: '245px',
                                borderRadius: '8px',
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '8px',
                                },
                                display: { xs: 'none', sm: 'flex' }
                            }}
                        />

                    </Box>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '20%',
                        }}
                    >
                        <FormControl
                            size="small"
                            sx={{
                                width: '250px',
                                height: '40px',
                                borderRadius: '8px',
                                bgcolor: 'rgba(240, 240, 240, 0.17)',
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '8px',
                                },
                            }}
                        >
                            <Select
                                value={selectedMonth}
                                onChange={handleMonthChange}
                                displayEmpty
                                size="small"
                                sx={{
                                    borderRadius: '8px',
                                }}
                            >
                                <MenuItem value="">
                                    <em>All Months</em>
                                </MenuItem>
                                <MenuItem value="01">January</MenuItem>
                                <MenuItem value="02">February</MenuItem>
                                <MenuItem value="03">March</MenuItem>
                                <MenuItem value="04">April</MenuItem>
                                <MenuItem value="05">May</MenuItem>
                                <MenuItem value="06">June</MenuItem>
                                <MenuItem value="07">July</MenuItem>
                                <MenuItem value="08">August</MenuItem>
                                <MenuItem value="09">September</MenuItem>
                                <MenuItem value="10">October</MenuItem>
                                <MenuItem value="11">November</MenuItem>
                                <MenuItem value="12">December</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>

                </Box>
                <Box
                    sx={{
                        mt: 5
                    }}
                >
                    <TableContainer sx={{ margin: 'auto', maxWidth: '95%' }}>
                        <Table>
                            <TableHead>
                                <TableRow sx={{ '& td, & th': { padding: '10px 14px' } }}>
                                    <TableCell sx={{ textAlign: 'left' }}><Typography variant="subtitle2" fontWeight="bold">Customer</Typography></TableCell>
                                    <TableCell align="right"><Typography variant="subtitle2" fontWeight="bold">TOTAL (LKR)</Typography></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {finalData.map((order) => (
                                    <TableRow
                                        key={order.id}
                                        sx={{
                                            '& td, & th': { padding: '10px 14px' },
                                        }}
                                    >

                                        <TableCell sx={{ textAlign: 'left', fontSize: '14px' }}>
                                            {order.fullname && order.fullname !== "Direct Customer" ? (
                                                <Link to={`/orders/${encodeURIComponent(order.fullname)}`} style={{ textDecoration: 'none' }}>
                                                    <Typography
                                                        sx={{
                                                            fontSize: '14px',
                                                            cursor: 'pointer',
                                                            color: '#636363',
                                                            '&:hover': {
                                                                color: 'blue'
                                                            }
                                                        }}
                                                    >
                                                        {order.fullname}
                                                    </Typography>
                                                </Link>
                                            ) : (
                                                <Typography sx={{ fontSize: '14px', color: '#636363' }}>
                                                    Direct Customer
                                                </Typography>
                                            )}
                                        </TableCell>


                                        <TableCell sx={{ textAlign: 'right', color: '#636363', fontSize: '14px' }}>
                                            {order.adjustedTotal.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
            </Box>
            {loading && (
                <Box sx={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100vw',
                    height: '100vh',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'white',
                    zIndex: 9999
                }}>
                    <l-trio
                        size="50"
                        speed="1.3"
                        color="red"
                    ></l-trio>
                </Box>
            )}
            <ScrollToTopButton />
        </Box>
    );
};

export default CustomerReport;
